import type { GateBucket, GateStatus } from "@iamu/shared";
import { Link } from "react-router-dom";
import { Kicker } from "@/components/ui/kicker";
import { Chip } from "@/components/ui/chip";
import { formatRelativeTime } from "@/lib/freshness";
import { markdownPreview } from "@/lib/plain-text";
import {
  BUCKET_LABELS,
  GateStatusChip,
  shortSha,
  bucketTone,
} from "@/components/gate/gate-vocabulary";
import type { FindingObservationItem } from "./finding-detail-types";

interface QualityGateHistoryItem {
  evaluationId: string;
  evaluatedAt: string;
  buildId: string;
  buildLabel: string;
  gitBranch?: string | null;
  gitSha?: string | null;
  environment: string;
  gateStatus: GateStatus;
  bucket: GateBucket;
}

interface FindingOccurrencesSectionProps {
  projectId: string;
  observations: FindingObservationItem[];
  gateHistory?: QualityGateHistoryItem[] | null;
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export function FindingOccurrencesSection({
  projectId,
  observations,
  gateHistory,
}: FindingOccurrencesSectionProps) {
  return (
    <>
      {/* Where it showed up — canonical observations across walkthroughs */}
      {observations.length > 0 && (
        <section className="space-y-4">
          <Kicker as="h2">
            Where it showed up · {observations.length}{" "}
            {observations.length === 1 ? "walkthrough" : "walkthroughs"}
          </Kicker>
          <ul className="divide-y divide-border/60">
            {observations.map((obs, i) => (
              <li
                key={obs.id}
                className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-4 py-3"
              >
                <time
                  dateTime={new Date(obs.createdAt).toISOString()}
                  className="w-20 shrink-0 text-xs tabular-nums text-muted-foreground"
                >
                  {formatRelativeTime(obs.createdAt)}
                </time>
                <Link
                  to={`/projects/${projectId}/walkthroughs/${obs.walkthroughId}${
                    typeof obs.stepNumber === "number"
                      ? `?tab=trace&step=${obs.stepNumber}`
                      : ""
                  }`}
                  className="min-w-0 flex-1 truncate text-sm underline-offset-4 transition-colors hover:text-primary hover:underline"
                >
                  {truncate(markdownPreview(obs.description), 100)}
                </Link>
                {typeof obs.stepNumber === "number" && (
                  <Chip tone="outline">Step {obs.stepNumber}</Chip>
                )}
                {i === 0 && <Chip tone="muted">Latest</Chip>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Quality gate history across builds */}
      {gateHistory && gateHistory.length > 0 && (
        <section className="space-y-4">
          <Kicker as="h2">
            Quality gate history · {gateHistory.length}{" "}
            {gateHistory.length === 1 ? "build" : "builds"}
          </Kicker>
          <ul className="divide-y divide-border/60">
            {gateHistory.map((gh) => (
              <li
                key={gh.evaluationId}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
              >
                <div className="flex flex-wrap items-center gap-3 min-w-0">
                  <time
                    dateTime={new Date(gh.evaluatedAt).toISOString()}
                    className="w-20 shrink-0 text-xs tabular-nums text-muted-foreground"
                  >
                    {formatRelativeTime(gh.evaluatedAt)}
                  </time>
                  <Link
                    to={`/projects/${projectId}/walkthroughs/builds/${gh.buildId}`}
                    className="font-medium text-sm underline-offset-4 hover:text-primary hover:underline truncate"
                  >
                    {gh.buildLabel}
                  </Link>
                  {(gh.gitBranch || gh.gitSha) && (
                    <span className="text-xs text-muted-foreground">
                      {[gh.gitBranch, shortSha(gh.gitSha)]
                        .filter(Boolean)
                        .join(" ")}
                    </span>
                  )}
                  <Chip tone="outline" className="text-xs">
                    {gh.environment}
                  </Chip>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <GateStatusChip status={gh.gateStatus} />
                  <Chip tone={bucketTone(gh.bucket)}>
                    {BUCKET_LABELS[gh.bucket] ?? gh.bucket}
                  </Chip>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
